<template>
  <div>
    <NBox
      v-if="
        !(directDebits.length && directDebits[0].enabled) &&
        !isLoadingDirectDebits &&
        !isErrorLoadingDirectDebits
      "
      v-gid="{
        module: 'cash',
        screen: 'home',
        section: 'depositDialog',
        component: 'directDebitDescriptionDialogButton',
      }"
      class="flex justify-between items-center !bg-blue-secondary py-3 px-2 mb-4 cursor-pointer"
      @click="
        () =>
          openDirectDebitDialog({
            isActiveDirectDebit: false,
          })
      "
    >
      <span class="inline-flex items-center">
        <NegarCreditCardIcon
          size="32"
          class="align-baseline text-blue-primary"
        />

        <NText weight="bold" class="text-blue-primary mr-2 align-bottom">
          {{ t('deposit.activateDirectDebit') }}
        </NText>
      </span>
    </NBox>

    <VeeForm v-slot="{ errors, handleSubmit, meta }" as="">
      <form
        autocomplete="off"
        data-testid="form"
        class="!w-[440px]"
        @submit="handleSubmit($event, onSubmit)"
      >
        <NText variant="caption1" weight="bold" class="block pr-3 mb-2">
          {{ t('deposit.depositRequest') }}
        </NText>

        <div class="bg-neutral-secondary p-4 rounded-md">
          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press block pr-3 mb-2"
          >
            {{ t('deposit.paymentGatewayMethod') }}
          </NText>

          <VeeField
            v-slot="{ field }"
            v-model="formModel.selectedGateway"
            name="selectedGateway"
            rules="required"
          >
            <NRadioGroup
              v-model="formModel.selectedGateway"
              v-bind="field"
              :error="errors.selectedGateway"
              :show-error="true"
              :required-sign="false"
              required
              data-testid="selected-gateway"
            >
              <NBox>
                <div class="flex flex-col items-center p-4">
                  <NRadio
                    v-if="directDebits.length && directDebits[0].enabled"
                    v-gid="{
                      module: 'cash',
                      screen: 'home',
                      section: 'depositDialog',
                      component: 'directDebitRadioButton',
                      disabled:
                        !directDebits.length || !directDebits[0].enabled,
                    }"
                    :value="PAYMENT_GATEWAYS.direct.value"
                    class="w-full border-b border-neutral-secondary pb-3"
                    :disabled="!directDebits.length || !directDebits[0].enabled"
                  >
                    <div class="flex items-center justify-between">
                      <span class="inline-flex items-center">
                        <NegarCreditCardIcon
                          v-if="isLoadingDirectDebits"
                          class="ml-1"
                        />
                        <DirectDebitIcon
                          v-else
                          class="ml-1 text-brand-primary"
                        />

                        <NText
                          variant="caption1"
                          weight="bold"
                          class="me-1"
                          :class="
                            directDebits.length
                              ? 'text-neutral-primary'
                              : 'text-gray-110'
                          "
                        >
                          {{ directDebitLabel }}
                        </NText>

                        <NegarInfoWithBorderIcon
                          size="20"
                          @click="
                            () =>
                              openDirectDebitDialog({
                                isActiveDirectDebit: true,
                              })
                          "
                        />
                      </span>

                      <NButtonIcon
                        v-gid="{
                          module: 'cash',
                          screen: 'home',
                          section: 'depositDialog',
                          component: `cancelDirectDebit`,
                        }"
                        size="mini"
                        color="gray"
                        class="mt-1"
                        @click="
                          () => openCancelDirectDebitDialog(directDebits[0].uId)
                        "
                      >
                        <NegarTrashLightIcon sise="16" />
                      </NButtonIcon>
                    </div>
                  </NRadio>

                  <NRadio
                    v-gid="{
                      module: 'cash',
                      screen: 'home',
                      section: 'depositDialog',
                      component: 'samanGatewayRadioButton',
                    }"
                    :value="PAYMENT_GATEWAYS.saman.value"
                    class="w-full first:pt-0 py-3 border-b border-neutral-secondary"
                  >
                    <div class="flex items-center">
                      <NegarSamanBankIcon class="ml-1" />

                      <NText
                        variant="caption1"
                        weight="bold"
                        class="text-neutral-primary"
                      >
                        {{ PAYMENT_GATEWAYS.saman.title }}
                      </NText>
                    </div>
                  </NRadio>

                  <NRadio
                    v-gid="{
                      module: 'cash',
                      screen: 'home',
                      section: 'depositDialog',
                      component: 'behPardakhtGatewayRadioButton',
                    }"
                    :value="PAYMENT_GATEWAYS.behPardakht.value"
                    class="w-full pt-3"
                  >
                    <div class="flex items-center">
                      <NegarMellatBankIcon class="ml-1" />

                      <NText
                        variant="caption1"
                        weight="bold"
                        class="text-neutral-primary"
                      >
                        {{ PAYMENT_GATEWAYS.behPardakht.title }}
                      </NText>
                    </div>
                  </NRadio>
                </div>
              </NBox>
            </NRadioGroup>
          </VeeField>

          <NText
            variant="caption1"
            weight="medium"
            class="text-neutral-secondary-press block pr-3 mt-5 mb-2"
          >
            {{ t('deposit.deposit_amount') }}
          </NText>

          <VeeField
            v-slot="{ field }"
            v-model="formModel.totalAmount"
            name="totalAmount"
            :rules="
              formModel.selectedGateway ===
              Number(PAYMENT_GATEWAYS.direct.value)
                ? {
                    required: true,
                    between: [
                      DEPOSIT_RANGE.minimum,
                      directDebits[0].withdrawCeiling,
                      t('deposit.deposit_amount'),
                      t('misc.rial'),
                    ],
                  }
                : {
                    required: true,
                    min: [
                      DEPOSIT_RANGE.minimum,
                      t('deposit.deposit_amount'),
                      t('misc.rial'),
                    ],
                  }
            "
          >
            <NInput
              v-model="formModel.totalAmount"
              v-mask="{
                alias: 'positiveNumber',
                showMaskOnHover: false,
              }"
              :placeholder="t('deposit.enter_amount')"
              input-class="text-right"
              v-bind="field"
              :error="errors.totalAmount"
              :show-error="true"
              :required-sign="false"
              type="tel"
              dir="ltr"
              required
              data-testid="total-amount"
              :change-value-on-mouse-wheel="false"
              @focusin="$event.target.select()"
            >
              <template #postfix>
                <NText variant="caption1" class="text-neutral-secondary-press">
                  {{ t('misc.rial') }}
                </NText>
              </template>
            </NInput>

            <NText
              :class="{
                invisible: !formModel.totalAmount,
              }"
              class="block text-center mb-4"
              variant="caption1"
              data-testid="amount-in-words"
            >
              {{ amountInWords }}
              {{ t('misc.toman') }}
            </NText>
          </VeeField>

          <NButton
            v-gid="{
              module: 'cash',
              screen: 'home',
              section: 'depositDialog',
              component: 'submitButton',
              disabled:
                isCreatingSamanPaymentToken ||
                isCreatingApPaymentToken ||
                isCreatingJibimoPaymentToken ||
                isCreateDirectDebit ||
                !meta.valid,
            }"
            class="w-full"
            :loading="
              isCreatingSamanPaymentToken ||
              isCreatingApPaymentToken ||
              isCreatingJibimoPaymentToken ||
              isCreateDirectDebit
            "
            :disabled="
              isCreatingSamanPaymentToken ||
              isCreatingApPaymentToken ||
              isCreatingJibimoPaymentToken ||
              isCreateDirectDebit ||
              !meta.valid
            "
            type="submit"
            data-testid="submit"
          >
            {{ t('deposit.pay') }}
          </NButton>
        </div>
      </form>
    </VeeForm>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { num2mixed } from 'num2persian';
import { useDialog, useToast } from '@negar/core';
import {
  useCreateSamanPaymentTokenQuery,
  createDepositRedirectURL,
  useCreateApPaymentTokenQuery,
  useGetDirectDebitsQuery,
  useCreateDirectDebitQuery,
  useCreateJibimoPaymentTokenQuery,
} from '@core/cash';
import {
  NegarCreditCardIcon,
  NegarInfoWithBorderIcon,
  NegarTrashLightIcon,
  NegarSamanBankIcon,
  NegarMellatBankIcon,
} from '@negar/icons';
import { useQueryClient } from '@libs/use';

import { useCashDialogs } from '@/modules/cash';

import vMask from '@/directives/mask';

import { DEPOSIT_RANGE, PAYMENT_GATEWAYS, BANKS } from '../constants';

const { t } = useI18n();
const toast = useToast();
const { remove } = useDialog();

const queryClient = useQueryClient();

const { openDirectDebitDialog, openCancelDirectDebitDialog } = useCashDialogs();

type FormModel = {
  selectedGateway: number;
  totalAmount: number | null;
};

const formModel = reactive<FormModel>({
  selectedGateway: PAYMENT_GATEWAYS.behPardakht.value,
  totalAmount: null,
});

const amountInWords = computed(() => {
  if (formModel.totalAmount !== null && formModel.totalAmount !== undefined) {
    return num2mixed(Math.round(formModel.totalAmount / 10));
  }

  return null;
});

const { directDebits, isLoadingDirectDebits, isErrorLoadingDirectDebits } =
  useGetDirectDebitsQuery();

const { createSamanPaymentToken, isCreatingSamanPaymentToken } =
  useCreateSamanPaymentTokenQuery({
    onSuccess(result) {
      if (result.data.token) {
        window.location.assign(createDepositRedirectURL(result.data.token));
      }
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const { createJibimoPaymentToken, isCreatingJibimoPaymentToken } =
  useCreateJibimoPaymentTokenQuery({
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const { createApPaymentToken, isCreatingApPaymentToken } =
  useCreateApPaymentTokenQuery({
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const { createDirectDebit, isCreateDirectDebit } = useCreateDirectDebitQuery({
  onSuccess() {
    toast.positive({
      message: t('deposit.directDebitSuccessMessage', {
        amount: formModel.totalAmount,
        bankName: BANKS[directDebits.value[0].bank].title,
        duration: 10000,
      }),
    });

    remove();

    queryClient.invalidateQueries(['cash']);
  },
  onReject(error) {
    if (error) {
      for (const { message } of error.response.data) {
        toast.negative({
          message,
          duration: 10000,
        });
      }
    }
  },
});

const onSubmit = () => {
  if (formModel.selectedGateway === Number(PAYMENT_GATEWAYS.saman.value)) {
    createSamanPaymentToken(formModel);

    return;
  }

  if (formModel.selectedGateway === PAYMENT_GATEWAYS.direct.value) {
    createDirectDebit({
      amount: formModel.totalAmount,
      traceId: directDebits.value[0].traceId,
    });

    return;
  }

  if (
    formModel.selectedGateway === Number(PAYMENT_GATEWAYS.behPardakht.value)
  ) {
    createJibimoPaymentToken(formModel);

    return;
  }

  createApPaymentToken(formModel);
};

const DirectDebitIcon = () => BANKS[directDebits.value[0].bank].icon;

const directDebitLabel = computed(() =>
  directDebits.value.length
    ? `${PAYMENT_GATEWAYS.direct.title} (${
        BANKS[directDebits.value[0]?.bank].title
      })`
    : `${PAYMENT_GATEWAYS.direct.title} (${t('misc.disabled')})`
);
</script>

<style>
.dialog-body > div {
  width: 100%;
}
</style>
